import { api } from './client'

/**
 * F-075 (TASK-0329): client di `LeadIntakeClientsController` (`/api/lead-intake-clients`, le chiavi con cui
 * un portale lead invia contatti a `POST /api/v1/leads`) e di `WebhooksController` (`/api/webhooks`, i
 * webhook in uscita firmati, il registro delle consegne). Tutto riservato all'Admin (403 agli altri).
 * La chiave API e il segreto del webhook arrivano UNA volta sola, nella risposta di creazione/rotazione
 * (`key` / `secret`): nessuna lettura li restituisce.
 */
export interface LeadIntakeClient {
  id: number
  name: string
  keyPrefix: string
  source: string
  leadProviderId: number | null
  campaignId: number | null
  isActive: boolean
  revokedAt: string | null
  revokedBy: number | null
  lastUsedAt: string | null
  perMinuteLimit: number
  /** F-112: quota giornaliera di lead (0 = illimitata); assente nelle risposte di creazione e rotazione. */
  dailyQuota?: number
  /** F-112: lead ricevuti oggi (giorno di Roma) con questa chiave. */
  usedToday?: number
  createdBy: number | null
  createdAt: string
  /** Presente SOLO nella risposta di creazione e di rotazione. */
  key?: string
}

export interface LeadIntakeClientBody {
  name: string
  source: string
  leadProviderId: number | null
  campaignId: number | null
  perMinuteLimit: number | null
}

export interface Webhook {
  id: number
  name: string
  url: string
  eventCodes: string[]
  isActive: boolean
  createdBy: number | null
  createdAt: string
  updatedAt: string | null
  /** Presente SOLO nella risposta di creazione e di rotazione. */
  secret?: string
}

export interface WebhookBody {
  name: string
  url: string
  eventCodes: string[]
  isActive?: boolean
}

export type WebhookDeliveryStatus = 'InCoda' | 'Consegnata' | 'Fallita' | 'Ritento'

export interface WebhookDelivery {
  id: number
  subscriptionId: number
  eventId: string
  eventType: string
  attempt: number
  status: WebhookDeliveryStatus
  httpStatus: number | null
  durationMs: number | null
  errorSnippet: string | null
  nextAttemptAt: string | null
  createdAt: string
  lastAttemptAt: string | null
}

export interface WebhookEventType {
  code: string
  label: string
  entityType: string
  attributes: { name: string; label: string }[]
}

export const WEBHOOK_STATUS_LABELS: Record<WebhookDeliveryStatus, string> = {
  InCoda: 'In coda',
  Consegnata: 'Consegnata',
  Fallita: 'Fallita',
  Ritento: 'Nuovo tentativo',
}

export const integrationsApi = {
  // Chiavi di acquisizione lead
  listClients: () => api<LeadIntakeClient[]>('/api/lead-intake-clients'),
  createClient: (body: LeadIntakeClientBody) =>
    api<LeadIntakeClient>('/api/lead-intake-clients', { method: 'POST', body: JSON.stringify(body) }),
  revokeClient: (id: number) =>
    api<LeadIntakeClient>(`/api/lead-intake-clients/${id}/revoke`, { method: 'POST' }),
  rotateClient: (id: number) =>
    api<LeadIntakeClient>(`/api/lead-intake-clients/${id}/rotate`, { method: 'POST' }),

  // Webhook in uscita
  eventTypes: () => api<WebhookEventType[]>('/api/webhooks/event-types'),
  listWebhooks: () => api<Webhook[]>('/api/webhooks'),
  createWebhook: (body: WebhookBody) =>
    api<Webhook>('/api/webhooks', { method: 'POST', body: JSON.stringify(body) }),
  updateWebhook: (id: number, body: WebhookBody) =>
    api<Webhook>(`/api/webhooks/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  rotateSecret: (id: number) => api<Webhook>(`/api/webhooks/${id}/rotate-secret`, { method: 'POST' }),
  testWebhook: (id: number) =>
    api<{ deliveryId: number; eventId: string; status: WebhookDeliveryStatus }>(
      `/api/webhooks/${id}/test`, { method: 'POST' }),
  removeWebhook: (id: number) => api<void>(`/api/webhooks/${id}`, { method: 'DELETE' }),

  // Registro delle consegne
  deliveries: (p: { subscriptionId?: number; status?: WebhookDeliveryStatus; limit?: number } = {}) => {
    const q = new URLSearchParams()
    if (p.subscriptionId) q.set('subscriptionId', String(p.subscriptionId))
    if (p.status) q.set('status', p.status)
    q.set('limit', String(p.limit ?? 50))
    return api<WebhookDelivery[]>(`/api/webhooks/deliveries?${q}`)
  },
  redeliver: (id: number) =>
    api<{ deliveryId: number; status: WebhookDeliveryStatus }>(
      `/api/webhooks/deliveries/${id}/redeliver`, { method: 'POST' }),

  // F-119: Centro integrazioni (salute, registro delle chiamate, manutenzione)
  center: () => api<IntegrationCenter>('/api/integrations/center'),
  centerCalls: (kind: IntegrationKind, id: number, offset = 0, limit = 50) =>
    api<IntegrationCallsPage>(`/api/integrations/center/${kind}/${id}/calls?offset=${offset}&limit=${limit}`),
  setMaintenance: (kind: IntegrationKind, id: number, body: { on: boolean; note?: string | null; until?: string | null }) =>
    api<{ kind: IntegrationKind; id: number }>(
      `/api/integrations/center/${kind}/${id}/maintenance`, { method: 'POST', body: JSON.stringify(body) }),
}

/**
 * F-119 (TASK-0373, REQ-225-001): `GET /api/integrations/center` (solo Admin). Lo stato si calcola dal vivo sulle
 * chiamate della finestra; con il registro spento (`callLogEnabled` false) ogni integrazione e' "SenzaTraffico".
 * `available` false = le tabelle del registro non ci sono ancora (migration da applicare): nessun errore 500.
 */
export type IntegrationKind = 'webhook' | 'lead_intake' | 'conti' | 'fatturazione' | 'drive'
export type IntegrationHealth = 'Operativa' | 'Degradata' | 'NonDisponibile' | 'Manutenzione' | 'SenzaTraffico'
export type IntegrationOutcome = 'Successo' | 'Fallito' | 'Parziale' | 'RichiedeIntervento'

export const INTEGRATION_HEALTH_LABELS: Record<IntegrationHealth, string> = {
  Operativa: 'Operativa',
  Degradata: 'Degradata',
  NonDisponibile: 'Non disponibile',
  Manutenzione: 'In manutenzione',
  SenzaTraffico: 'Senza traffico',
}

export interface IntegrationCenterItem {
  kind: IntegrationKind
  id: number
  name: string
  type: string
  direction: 'in' | 'out'
  active: boolean
  health: IntegrationHealth
  lastSuccessAt: string | null
  lastUsedAt: string | null
  lastError: { at: string; code: string | null; snippet: string | null; httpStatus: number | null } | null
  calls24h: number
  successRatePct: number | null
  avgDurationMs: number | null
  /** Consegne in coda o in ritento (webhook); `null` = n/d (intake). */
  queue: number | null
  /** Tentativi oltre il primo nelle ultime 24 ore. */
  retries24h: number
  maintenance: { on: boolean; note: string | null; until: string | null }
}

export interface IntegrationCenter {
  available: boolean
  generatedAt: string
  callLogEnabled: boolean
  healthSweeperEnabled: boolean
  windowMinutes?: number
  thresholds?: { degradedFailureRatePct: number; minSample: number; unavailableConsecutiveFailures: number }
  items: IntegrationCenterItem[]
}

export interface IntegrationCall {
  id: number
  occurredAt: string
  direction: 'in' | 'out'
  outcome: IntegrationOutcome
  httpStatus: number | null
  durationMs: number | null
  attempt: number | null
  errorCode: string | null
  errorSnippet: string | null
  entityType: string | null
  entityId: number | null
  /** R3-26: la richiesta fatta, per nome (mai un corpo). */
  operation: string | null
  /** R3-26: la pratica a cui la chiamata si riferisce, se nota. */
  contractId: number | null
}

export interface IntegrationCallsPage {
  available: boolean
  total: number
  offset: number
  limit: number
  items: IntegrationCall[]
}

/**
 * Z-10/Z-11: moduli Lead Ads di Google Ads e Meta (`/api/lead-ads-forms`, solo Admin). L'URL e la chiave da
 * incollare in Google Ads si leggono dal dettaglio (`GET /{id}`, registrato nel registro di controllo).
 */
export interface LeadAdsForm {
  id: number
  platform: 'google_ads' | 'meta_ads'
  name: string
  externalRef: string | null
  campaignId: number | null
  isActive: boolean
  lastReceivedAt: string | null
  createdAt: string
  webhookUrl: string
  hasKey: boolean
  /** Solo nel dettaglio e dopo la rotazione. */
  key?: string | null
}

export interface LeadAdsFormsList {
  items: LeadAdsForm[]
  meta: {
    webhookUrl: string
    verifyTokenConfigured: boolean
    appSecretConfigured: boolean
    pageTokenConfigured: boolean
  }
}

export const leadAdsApi = {
  list: () => api<LeadAdsFormsList>('/api/lead-ads-forms'),
  get: (id: number) => api<LeadAdsForm>(`/api/lead-ads-forms/${id}`),
  create: (body: { platform: string; name: string; externalRef: string | null; campaignId: number | null }) =>
    api<LeadAdsForm>('/api/lead-ads-forms', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: { name: string; campaignId: number | null; isActive: boolean }) =>
    api<LeadAdsForm>(`/api/lead-ads-forms/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  rotateKey: (id: number) => api<LeadAdsForm>(`/api/lead-ads-forms/${id}/rotate-key`, { method: 'POST' }),
}
